import React, { useEffect, useState } from 'react';
import { Check, Copy, Share } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { t } from '../../i18n';
import Modal from '../ui/Modal';
import { canNativeShare, copyText, shareInvite } from '../../utils/inviteShare';

export function joinUrlForRoom(roomCode, origin) {
  const code = String(roomCode || '').trim().toUpperCase();
  if (!code) return '';
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  return base ? `${base}/join/${code}` : `/join/${code}`;
}

/**
 * In-game invite modal (host only). Same centered chrome as Host tools /
 * Waiting — never a bottom sheet. Room code + lobby join QR + Copy link,
 * and native Share when the browser has it. Sized to fit 375×667 without
 * scrolling.
 */
export function InviteSheet({ open, roomCode, onClose }) {
  const [copied, setCopied] = useState(false);
  const joinUrl = joinUrlForRoom(roomCode);
  const canShare = canNativeShare();

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  const handleCopy = async () => {
    try {
      await copyText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.warn('Copy failed:', err);
    }
  };

  const handleShare = async () => {
    try {
      const result = await shareInvite({ url: joinUrl });
      if (result === 'copied') {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    } catch (err) {
      console.warn('Share failed:', err);
    }
  };

  return (
    <Modal
      open={!!open && !!roomCode}
      onClose={onClose}
      title={t('invite.addPlayers')}
      compact
      zClass="z-[90]"
      testId="invite-sheet"
    >
      <div className="text-center">
        <div className="label-eyebrow text-mute">{t('home.roomCode')}</div>
        <div
          className="mt-1.5 text-[34px] font-semibold tnum tracking-[0.16em] text-ink leading-none"
          data-testid="invite-room-code"
        >
          {roomCode}
        </div>

        {joinUrl ? (
          <div
            className="mt-3 inline-block bg-white p-1.5 rounded-xl border border-line"
            data-testid="invite-qr"
          >
            <QRCodeSVG
              value={joinUrl}
              size={120}
              bgColor="#ffffff"
              fgColor="#111111"
              level="M"
              includeMargin={false}
            />
          </div>
        ) : null}

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={handleCopy}
            className="flex-1 h-12 rounded-[12px] border border-line bg-transparent text-ink text-[14px] font-medium inline-flex items-center justify-center gap-1.5"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? t('common.copied') : t('common.copy')}
          </button>
          {canShare && (
            <button
              type="button"
              onClick={handleShare}
              className="flex-1 h-12 rounded-[12px] bg-ink text-cream text-[14px] font-semibold inline-flex items-center justify-center gap-1.5"
            >
              <Share className="w-4 h-4" />
              {t('common.share')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
